// « Mains vives » (D-164) : le plein écran du jeu, dessiné sur UN SEUL canevas, celui qu'on voit et celui que la vidéo
// enregistre (captureStream) : la vidéo garde donc l'image, les cibles, les mains et les chiffres. 390 × 844 comme les
// maquettes c3 à c5 : en haut (188) la ligne d'enregistrement et les trois gros chiffres (ou le calage), au milieu l'image de
// la caméra en 3:4 (520), en bas (136) la cible annoncée et le temps qui reste. Toujours sombre (D-098).
import { CAM_H, CAM_W, COLORS, STAGE_H, STAGE_W, TARGET_R, TOP_H, type Pt, type ShapeKey, type TargetKey } from '../domain/mains-vives';
import { JERSEY, LIMB, SHOE, SHORTS, TRIM, type Pose } from './mv-art';

type G = CanvasRenderingContext2D;
const DISPLAY = 'Anton, Impact, "Arial Narrow", sans-serif';
const MONO = '"JetBrains Mono", ui-monospace, Menlo, Consolas, monospace';
const SANS = '"Inter Variable", Inter, -apple-system, "Segoe UI", Roboto, sans-serif';

export type TargetLook = 'dash' | 'idle' | 'lit' | 'dim';
export interface StageTarget extends Pt { key: TargetKey; color: number; look: TargetLook; flash?: number }
/** Une main repérée ; au niveau 4 (D-165), la main qui dribble porte « DRIBBLE », en vert tant que le dribble continue. */
export interface StageHand extends Pt { label?: string; dim?: boolean; trail?: Pt[]; tone?: 'ok' | 'warn' }
export type Bottom =
  | { kind: 'go'; title: string; sub: string; redo: boolean }
  | { kind: 'count'; title: string; sub: string; step: number }
  | { kind: 'call'; color: string; ink: string; shape?: ShapeKey; arrow?: string; word: string; left: string };
export interface StageState {
  label: string;
  rec: string | null;
  armed: boolean;
  status?: { ok: boolean; title: string; sub: string };
  nums?: { hits: string; avg: string; streak: string; dim: boolean };
  camera: (g: G, w: number, h: number) => void;
  targets: StageTarget[];
  hands: StageHand[];
  checks?: { ok: boolean; text: string }[];
  count?: number | null;
  floats?: { x: number; y: number; text: string; sub: string; alpha: number }[];
  pause?: { title: string; sub: string } | null;
  bottom: Bottom;
  glow?: string | null;
  prog?: number | null;
  /** D-165 : un rappel bien visible en haut de l'image (« Fléchis les jambes »). */
  alert?: string | null;
}

function rr(g: G, x: number, y: number, w: number, h: number, r: number): void {
  const k = Math.min(r, w / 2, h / 2);
  g.beginPath();
  g.moveTo(x + k, y); g.arcTo(x + w, y, x + w, y + h, k); g.arcTo(x + w, y + h, x, y + h, k); g.arcTo(x, y + h, x, y, k); g.arcTo(x, y, x + w, y, k);
  g.closePath();
}
const spacing = (g: G, px: number) => { (g as unknown as { letterSpacing?: string }).letterSpacing = `${px}px`; };
function text(g: G, s: string, x: number, y: number, font: string, color: string, align: CanvasTextAlign = 'left', ls = 0): number {
  g.font = font; g.fillStyle = color; g.textAlign = align; g.textBaseline = 'alphabetic'; spacing(g, ls);
  g.fillText(s, x, y);
  const w = g.measureText(s).width;
  spacing(g, 0);
  return w;
}
function wrap(g: G, s: string, font: string, max: number): string[] {
  g.font = font;
  const out: string[] = [];
  let line = '';
  for (const w of s.split(' ')) {
    const t = line ? `${line} ${w}` : w;
    if (g.measureText(t).width > max && line) { out.push(line); line = w; } else line = t;
  }
  if (line) out.push(line);
  return out;
}
export function shape(g: G, k: ShapeKey, x: number, y: number, s: number, fill: string): void {
  g.fillStyle = fill; g.strokeStyle = fill; g.lineJoin = 'round';
  g.beginPath();
  if (k === 'rond') { g.arc(x, y, s * 0.8, 0, Math.PI * 2); g.fill(); return; }
  if (k === 'carre') { rr(g, x - s * 0.75, y - s * 0.75, s * 1.5, s * 1.5, s * 0.18); g.fill(); return; }
  if (k === 'tri') { g.moveTo(x, y - s * 0.95); g.lineTo(x + s, y + s * 0.75); g.lineTo(x - s, y + s * 0.75); g.closePath(); g.lineWidth = s * 0.25; g.fill(); g.stroke(); return; }
  g.moveTo(x, y - s); g.lineTo(x + s, y); g.lineTo(x, y + s); g.lineTo(x - s, y); g.closePath(); g.lineWidth = s * 0.2; g.fill(); g.stroke();
}
function check(g: G, x: number, y: number, s: number, color: string): void {
  g.strokeStyle = color; g.lineWidth = s * 0.14; g.lineCap = 'round'; g.lineJoin = 'round';
  g.beginPath(); g.moveTo(x - s * 0.35, y); g.lineTo(x - s * 0.08, y + s * 0.28); g.lineTo(x + s * 0.38, y - s * 0.25); g.stroke();
}
function arrowGlyph(g: G, a: string, x: number, y: number, s: number, color: string): void {
  const dir: Record<string, [number, number]> = { '↖': [-1, -1], '↗': [1, -1], '↙': [-1, 1], '↘': [1, 1] };
  const [dx, dy] = dir[a] ?? [1, -1];
  const ux = dx / Math.SQRT2, uy = dy / Math.SQRT2;
  g.strokeStyle = color; g.lineWidth = s * 0.16; g.lineCap = 'round'; g.lineJoin = 'round';
  g.beginPath(); g.moveTo(x - ux * s * 0.55, y - uy * s * 0.55); g.lineTo(x + ux * s * 0.55, y + uy * s * 0.55); g.stroke();
  g.beginPath(); g.moveTo(x + ux * s * 0.55 - dx * s * 0.42, y + uy * s * 0.55); g.lineTo(x + ux * s * 0.55, y + uy * s * 0.55); g.lineTo(x + ux * s * 0.55, y + uy * s * 0.55 - dy * s * 0.42); g.stroke();
}

function drawTarget(g: G, t: StageTarget): void {
  const c = COLORS[t.color]!;
  g.save();
  if (t.look === 'lit') {
    g.lineWidth = 4; g.strokeStyle = c.hex; g.globalAlpha = 0.22; g.beginPath(); g.arc(t.x, t.y, TARGET_R + 24, 0, Math.PI * 2); g.stroke();
    g.lineWidth = 5; g.globalAlpha = 0.55; g.beginPath(); g.arc(t.x, t.y, TARGET_R + 11, 0, Math.PI * 2); g.stroke();
    g.globalAlpha = 1; g.fillStyle = c.hex; g.beginPath(); g.arc(t.x, t.y, TARGET_R + 2, 0, Math.PI * 2); g.fill();
    g.lineWidth = 4; g.strokeStyle = '#FFFFFF'; g.stroke();
    shape(g, c.shape, t.x, t.y, 12, c.ink);
  } else {
    g.globalAlpha = t.look === 'dim' ? 0.5 : 1;
    g.fillStyle = c.hex; g.globalAlpha *= t.look === 'dash' ? 0.22 : 0.28;
    g.beginPath(); g.arc(t.x, t.y, TARGET_R, 0, Math.PI * 2); g.fill();
    g.globalAlpha = t.look === 'dim' ? 0.5 : 1;
    g.lineWidth = 3.5; g.strokeStyle = c.hex; if (t.look === 'dash') g.setLineDash([7, 6]);
    g.stroke(); g.setLineDash([]);
    shape(g, c.shape, t.x, t.y, 10, '#FFFFFF');
  }
  if (t.flash) {
    g.globalAlpha = Math.max(0, t.flash); g.strokeStyle = '#FFFFFF'; g.lineWidth = 5;
    g.beginPath(); g.arc(t.x, t.y, TARGET_R + 8 + (1 - t.flash) * 26, 0, Math.PI * 2); g.stroke();
  }
  g.restore();
}
function drawHand(g: G, h: StageHand): void {
  g.save();
  g.globalAlpha = h.dim ? 0.6 : 1;
  (h.trail ?? []).forEach((p, i) => { g.fillStyle = `rgba(255,255,255,${0.16 + i * 0.14})`; g.beginPath(); g.arc(p.x, p.y, 5 + i * 2, 0, Math.PI * 2); g.fill(); });
  const ink = h.tone === 'ok' ? '#4ADE80' : h.tone === 'warn' ? '#FBBF24' : '#FFFFFF';
  g.fillStyle = 'rgba(255,255,255,.14)'; g.strokeStyle = ink; g.lineWidth = 3;
  g.beginPath(); g.arc(h.x, h.y, 21, 0, Math.PI * 2); g.fill(); g.stroke();
  g.fillStyle = ink; g.beginPath(); g.arc(h.x, h.y, 4.5, 0, Math.PI * 2); g.fill();
  g.lineCap = 'round';
  for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]] as const) { g.beginPath(); g.moveTo(h.x + dx * 25, h.y + dy * 25); g.lineTo(h.x + dx * 31, h.y + dy * 31); g.stroke(); }
  if (h.label) text(g, h.label, h.x, h.y + 50, `800 11px ${MONO}`, ink, 'center', 1);
  g.restore();
}

/** Dessine tout l'écran du jeu ; `k` : pixels du canevas par unité (2 : 780 × 1688). */
export function drawStage(g: G, s: StageState, k: number): void {
  g.setTransform(k, 0, 0, k, 0, 0);
  g.fillStyle = '#000'; g.fillRect(0, 0, STAGE_W, STAGE_H);

  // --- L'image de la caméra, ses incrustations ---------------------------------------------------------------------------
  g.save();
  g.translate(0, TOP_H);
  g.beginPath(); g.rect(0, 0, CAM_W, CAM_H); g.clip();
  g.fillStyle = '#111'; g.fillRect(0, 0, CAM_W, CAM_H);
  s.camera(g, CAM_W, CAM_H);
  const v = g.createRadialGradient(195, 239, 0, 195, 239, 0.72 * Math.hypot(CAM_W, CAM_H) / 1.2);
  v.addColorStop(0.6, 'rgba(0,0,0,0)'); v.addColorStop(1, 'rgba(0,0,0,.5)');
  g.fillStyle = v; g.fillRect(0, 0, CAM_W, CAM_H);
  if (s.count != null || s.pause) { g.fillStyle = 'rgba(0,0,0,.38)'; g.fillRect(0, 0, CAM_W, CAM_H); }
  for (const t of s.targets) drawTarget(g, t);
  for (const h of s.hands) drawHand(g, h);
  for (const f of s.floats ?? []) {
    g.save(); g.globalAlpha = f.alpha; g.shadowColor = 'rgba(0,0,0,.6)'; g.shadowBlur = 10; g.shadowOffsetY = 2;
    text(g, f.text, f.x, f.y + 26, `400 30px ${DISPLAY}`, '#FFFFFF');
    text(g, f.sub, f.x, f.y + 46, `800 13px ${MONO}`, '#FFFFFF', 'left', 0.5);
    g.restore();
  }
  if (s.checks?.length) {
    g.font = `700 12.5px ${SANS}`;
    // Une ligne de pastilles ; trop large pour l'image (D-165 : « En position » de plus), deux lignes.
    const all = s.checks.map(c => ({ c, w: g.measureText(c.text).width + 20 + 20 }));
    const rows: (typeof all)[] = [[]];
    for (const it of all) {
      const row = rows[rows.length - 1]!;
      if (row.length && row.reduce((a, b) => a + b.w + 6, 0) + it.w > CAM_W - 16) rows.push([it]); else row.push(it);
    }
    rows.forEach((row, r) => {
      const total = row.reduce((a, b) => a + b.w, 0) + 6 * (row.length - 1);
      let x = (CAM_W - total) / 2;
      const y = CAM_H - 10 - 30 - (rows.length - 1 - r) * 36;
      for (const { c, w } of row) {
        rr(g, x, y, w, 30, 15); g.fillStyle = 'rgba(0,0,0,.62)'; g.fill(); g.strokeStyle = 'rgba(255,255,255,.2)'; g.lineWidth = 1; g.stroke();
        if (c.ok) check(g, x + 17, y + 15, 15, '#4ADE80');
        else text(g, '!', x + 17, y + 20, `800 15px ${SANS}`, '#FBBF24', 'center');
        text(g, c.text, x + 30, y + 19.5, `700 12.5px ${SANS}`, '#FFFFFF');
        x += w + 6;
      }
    });
  }
  if (s.alert) {
    g.font = `400 26px ${DISPLAY}`;
    const w = g.measureText(s.alert.toUpperCase()).width + 58;
    const x = (CAM_W - w) / 2;
    rr(g, x, 12, w, 44, 22); g.fillStyle = '#FBBF24'; g.fill();
    text(g, '!', x + 22, 44, `400 26px ${DISPLAY}`, '#3B2A02', 'center');
    text(g, s.alert.toUpperCase(), x + 38, 44, `400 26px ${DISPLAY}`, '#3B2A02');
  }
  if (s.count != null) {
    g.fillStyle = 'rgba(0,0,0,.35)'; g.beginPath(); g.arc(195, 260, 115, 0, Math.PI * 2); g.fill();
    g.strokeStyle = 'rgba(255,255,255,.85)'; g.lineWidth = 6; g.stroke();
    text(g, String(s.count), 195, 260 + 70, `400 190px ${DISPLAY}`, '#FFFFFF', 'center');
  }
  if (s.pause) {
    rr(g, 24, 190, CAM_W - 48, 140, 18); g.fillStyle = 'rgba(14,15,17,.92)'; g.fill();
    text(g, s.pause.title.toUpperCase(), 195, 240, `400 30px ${DISPLAY}`, '#FFFFFF', 'center');
    wrap(g, s.pause.sub, `500 14px ${SANS}`, CAM_W - 90).forEach((l, i) => text(g, l, 195, 270 + i * 19, `500 14px ${SANS}`, 'rgba(255,255,255,.8)', 'center'));
  }
  g.restore();

  // --- En haut : la ligne d'enregistrement, ce qui tourne, la croix ; puis le calage ou les trois chiffres --------------------
  let lx = 14;
  if (s.rec) {
    g.font = `800 13px ${MONO}`; spacing(g, 0.8);
    const w = 12 + 12 + 8 + g.measureText(s.rec).width + 12; spacing(g, 0);
    rr(g, 14, 19, w, 34, 17); g.fillStyle = 'rgba(239,68,68,.18)'; g.fill();
    g.fillStyle = 'rgba(239,68,68,.28)'; g.beginPath(); g.arc(14 + 18, 36, 10, 0, Math.PI * 2); g.fill();
    g.fillStyle = '#EF4444'; g.beginPath(); g.arc(14 + 18, 36, 6, 0, Math.PI * 2); g.fill();
    text(g, s.rec, 14 + 32, 40.5, `800 13px ${MONO}`, '#FCA5A5', 'left', 0.8);
    lx = 14 + w;
  }
  const label = s.label.toUpperCase();
  const right = s.armed ? 250 : 330;
  g.font = `800 11.5px ${MONO}`;
  if (s.rec) text(g, label, (lx + right) / 2, 40, `800 11.5px ${MONO}`, 'rgba(255,255,255,.55)', 'center', 1.15);
  else text(g, label, 14, 40, `800 11.5px ${MONO}`, 'rgba(255,255,255,.55)', 'left', 1.15);
  if (s.armed) {
    rr(g, 250, 13, 126, 46, 23); g.fillStyle = '#EF4444'; g.fill();
    text(g, 'Arrêter ?', 313, 41.5, `800 16px ${SANS}`, '#FFFFFF', 'center');
  } else {
    g.fillStyle = 'rgba(255,255,255,.16)'; g.beginPath(); g.arc(353, 36, 23, 0, Math.PI * 2); g.fill();
    g.strokeStyle = '#FFFFFF'; g.lineWidth = 2.6; g.lineCap = 'round';
    g.beginPath(); g.moveTo(346, 29); g.lineTo(360, 43); g.moveTo(360, 29); g.lineTo(346, 43); g.stroke();
  }
  if (s.status) {
    const ok = s.status.ok;
    g.font = `400 34px ${DISPLAY}`;
    const tw = g.measureText(s.status.title.toUpperCase()).width;
    const w = 12 + 30 + 10 + tw + 16;
    rr(g, 14, 76, w, 56, 16); g.fillStyle = ok ? '#22C55E' : '#FBBF24'; g.fill();
    if (ok) check(g, 14 + 12 + 15, 104, 30, '#052E16'); else text(g, '!', 14 + 12 + 15, 117, `400 34px ${DISPLAY}`, '#3B2A02', 'center');
    text(g, s.status.title.toUpperCase(), 14 + 12 + 30 + 10, 117, `400 34px ${DISPLAY}`, ok ? '#052E16' : '#3B2A02');
    wrap(g, s.status.sub, `400 14.5px ${SANS}`, STAGE_W - 28).slice(0, 2).forEach((l, i, all) => text(g, l, 14, (all.length > 1 ? 155 : 160) + i * 19, `400 14.5px ${SANS}`, 'rgba(255,255,255,.78)'));
  }
  if (s.nums) {
    const col = s.nums.dim ? 'rgba(255,255,255,.28)' : '#FFFFFF';
    const cx = [14 + 104.93 / 2, 14 + 104.93 + 152.2 / 2, 14 + 104.93 + 152.2 + 104.93 / 2];
    const [a, b, c] = [s.nums.hits, s.nums.avg, s.nums.streak];
    text(g, a, cx[0]!, 148, `400 76px ${DISPLAY}`, col, 'center', 0.7);
    // la moyenne et son petit « s »
    const unit = b !== '–' ? 's' : '';
    g.font = `400 76px ${DISPLAY}`; spacing(g, 0.7);
    const bw = g.measureText(b).width; spacing(g, 0);
    g.font = `400 26px ${DISPLAY}`;
    const uw = unit ? g.measureText(unit).width + 3 : 0;
    const bx = cx[1]! - (bw + uw) / 2;
    text(g, b, bx, 148, `400 76px ${DISPLAY}`, col, 'left', 0.7);
    if (unit) text(g, unit, bx + bw + 3, 148, `400 26px ${DISPLAY}`, s.nums.dim ? col : 'rgba(255,255,255,.6)');
    text(g, c, cx[2]!, 148, `400 76px ${DISPLAY}`, col, 'center', 0.7);
    ['touchées', 'moyenne', 'série'].forEach((l, i) => text(g, l.toUpperCase(), cx[i]!, 172, `800 12px ${MONO}`, 'rgba(255,255,255,.6)', 'center', 1.44));
  }

  // --- En bas -------------------------------------------------------------------------------------------------------------
  const by = TOP_H + CAM_H, mid = by + 4 + (136 - 18) / 2;
  const b = s.bottom;
  if (b.kind === 'go' || b.kind === 'count') {
    const maxW = b.kind === 'go' && b.redo ? 230 : 280;
    const lines = wrap(g, b.title.toUpperCase(), `400 36px ${DISPLAY}`, maxW);
    const subLines = wrap(g, b.sub, `400 13.5px ${SANS}`, b.kind === 'count' ? 260 : 330);
    const h = lines.length * 34 + 4 + subLines.length * 18;
    let y = mid - h / 2 + 30;
    for (const l of lines) { text(g, l, 18, y, `400 36px ${DISPLAY}`, '#FFFFFF'); y += 34; }
    y += 2;
    for (const l of subLines) { text(g, l, 18, y, `400 13.5px ${SANS}`, 'rgba(255,255,255,.7)'); y += 18; }
    if (b.kind === 'go' && b.redo) {
      rr(g, 272, mid - 23, 100, 46, 14); g.fillStyle = 'rgba(255,255,255,.14)'; g.fill(); g.strokeStyle = 'rgba(255,255,255,.22)'; g.lineWidth = 1; g.stroke();
      g.strokeStyle = '#FFFFFF'; g.lineWidth = 2; g.lineCap = 'round';
      g.beginPath(); g.arc(294, mid, 7, -0.3, Math.PI * 1.55); g.stroke();
      g.beginPath(); g.moveTo(301, mid - 9); g.lineTo(301, mid - 2); g.lineTo(294, mid - 2); g.stroke();
      text(g, 'Refaire', 310, mid + 5, `700 14px ${SANS}`, '#FFFFFF');
    }
    if (b.kind === 'count') {
      for (let i = 0; i < 3; i++) { g.fillStyle = i < b.step ? '#FFFFFF' : 'rgba(255,255,255,.25)'; g.beginPath(); g.arc(372 - 5 - (2 - i) * 18, mid - 8, 5, 0, Math.PI * 2); g.fill(); }
      text(g, '3 · 2 · 1', 372, mid + 16, `800 11px ${MONO}`, 'rgba(255,255,255,.55)', 'right', 1.1);
    }
  } else {
    const sx = 18, sy = mid - 32;
    rr(g, sx - 7, sy - 7, 78, 78, 22); g.fillStyle = b.color; g.fill();
    rr(g, sx - 4, sy - 4, 72, 72, 19); g.fillStyle = '#000'; g.fill();
    rr(g, sx, sy, 64, 64, 16); g.fillStyle = b.color; g.fill();
    if (b.arrow) arrowGlyph(g, b.arrow, sx + 32, sy + 32, 40, b.ink);
    else if (b.shape) shape(g, b.shape, sx + 32, sy + 32, 15, b.ink);
    text(g, b.word.toUpperCase(), sx + 64 + 14, mid + 26, `400 70px ${DISPLAY}`, b.color, 'left', 0.7);
    text(g, b.left, 372, mid + 4, `800 30px ${MONO}`, '#FFFFFF', 'right', -0.6);
    text(g, 'RESTE', 372, mid + 26, `800 11px ${MONO}`, 'rgba(255,255,255,.55)', 'right', 1.1);
  }

  // --- La cible annoncée : tout l'écran s'entoure de sa couleur ; la barre du temps ---------------------------------------
  if (s.glow) {
    g.save();
    const edge = (x0: number, y0: number, x1: number, y1: number, rx: number, ry: number, rw: number, rh: number) => {
      const gr = g.createLinearGradient(x0, y0, x1, y1);
      gr.addColorStop(0, hexA(s.glow!, 0.5)); gr.addColorStop(1, hexA(s.glow!, 0));
      g.fillStyle = gr; g.fillRect(rx, ry, rw, rh);
    };
    edge(0, 0, 70, 0, 0, 0, 70, STAGE_H); edge(STAGE_W, 0, STAGE_W - 70, 0, STAGE_W - 70, 0, 70, STAGE_H);
    edge(0, 0, 0, 70, 0, 0, STAGE_W, 70); edge(0, STAGE_H, 0, STAGE_H - 70, 0, STAGE_H - 70, STAGE_W, 70);
    g.strokeStyle = s.glow; g.lineWidth = 7; g.strokeRect(3.5, 3.5, STAGE_W - 7, STAGE_H - 7);
    g.restore();
  }
  if (s.prog != null) {
    g.fillStyle = 'rgba(255,255,255,.14)'; g.fillRect(0, STAGE_H - 7, STAGE_W, 7);
    g.fillStyle = '#FFFFFF'; g.fillRect(0, STAGE_H - 7, STAGE_W * Math.max(0, Math.min(1, s.prog)), 7);
  }
}
const hexA = (hex: string, a: number) => { const n = parseInt(hex.slice(1), 16); return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${a})`; };

// --- Le mode d'essai (tests, captures) : l'image de la caméra dessinée, la pièce et la silhouette des maquettes ----------------
let roomCache: HTMLCanvasElement | null = null;
function roomCanvas(): HTMLCanvasElement {
  if (roomCache) return roomCache;
  const c = document.createElement('canvas');
  c.width = CAM_W * 2; c.height = CAM_H * 2;
  const g = c.getContext('2d')!;
  g.scale(2, 2);
  const w = g.createLinearGradient(0, 0, 0, 330); w.addColorStop(0, '#454C5B'); w.addColorStop(1, '#363C48');
  g.fillStyle = w; g.fillRect(0, 0, 390, 330);
  const l = g.createRadialGradient(195, 115, 0, 195, 115, 195); l.addColorStop(0, 'rgba(255,246,224,.16)'); l.addColorStop(1, 'rgba(255,246,224,0)');
  g.fillStyle = l; g.fillRect(0, 0, 390, 330);
  g.fillStyle = '#3A404C'; g.strokeStyle = '#2B3039'; g.lineWidth = 5; rr(g, 8, 104, 62, 220, 3); g.fill(); g.stroke();
  g.fillStyle = '#2B3039'; g.beginPath(); g.arc(58, 220, 3.5, 0, Math.PI * 2); g.fill();
  g.fillStyle = '#3E4553'; g.lineWidth = 4; rr(g, 296, 176, 60, 46, 3); g.fill(); g.stroke();
  g.strokeStyle = '#56607A'; g.lineWidth = 3; g.beginPath(); g.moveTo(304, 214); g.lineTo(320, 196); g.lineTo(331, 207); g.lineTo(340, 198); g.lineTo(349, 214); g.stroke();
  const f = g.createLinearGradient(0, 330, 0, 520); f.addColorStop(0, '#5E4E3E'); f.addColorStop(1, '#4A3D31');
  g.fillStyle = f; g.fillRect(0, 330, 390, 190);
  g.strokeStyle = 'rgba(0,0,0,.14)'; g.lineWidth = 2;
  for (const xb of [-260, -90, 80, 250, 420, 590, 760]) { const t = (330 - 200) / (520 - 200); g.beginPath(); g.moveTo(195 + (xb - 195) * t, 330); g.lineTo(xb, 520); g.stroke(); }
  g.fillStyle = '#2B3039'; g.fillRect(0, 320, 390, 10);
  g.fillStyle = '#C2410C'; g.beginPath(); g.arc(346, 452, 17, 0, Math.PI * 2); g.fill();
  g.strokeStyle = '#17191C'; g.lineWidth = 1.6; g.beginPath();
  g.moveTo(329, 452); g.lineTo(363, 452); g.moveTo(346, 435); g.lineTo(346, 469);
  g.moveTo(334, 440); g.quadraticCurveTo(346, 452, 334, 464); g.moveTo(358, 440); g.quadraticCurveTo(346, 452, 358, 464); g.stroke();
  roomCache = c;
  return c;
}
/** La silhouette des maquettes, au canevas, pour une pose donnée. */
export function drawBody(g: G, p: Pose, num = '7'): void {
  const limb = (a: number[], b: number[], c: number[], w: number) => {
    g.strokeStyle = LIMB; g.lineWidth = w; g.lineCap = 'round'; g.lineJoin = 'round';
    g.beginPath(); g.moveTo(a[0]!, a[1]!); g.lineTo(b[0]!, b[1]!); g.lineTo(c[0]!, c[1]!); g.stroke();
  };
  const poly = (ps: number[][], fill: string, w: number) => {
    g.fillStyle = fill; g.strokeStyle = fill; g.lineWidth = w; g.lineJoin = 'round';
    g.beginPath(); ps.forEach((q, i) => (i ? g.lineTo(q[0]!, q[1]!) : g.moveTo(q[0]!, q[1]!))); g.closePath(); g.fill(); g.stroke();
  };
  const lerp = (a: number[], b: number[], t: number) => [a[0]! + (b[0]! - a[0]!) * t, a[1]! + (b[1]! - a[1]!) * t];
  const [hL, hR] = p.hip, [sL, sR] = p.sh;
  const crotch = [(hL[0] + hR[0]) / 2, (hL[1] + hR[1]) / 2 + 24];
  const tL = lerp(hL, p.kn[0], 0.42), tR = lerp(hR, p.kn[1], 0.42);
  const mid = [(sL[0] + sR[0] + hL[0] + hR[0]) / 4, (sL[1] + sR[1] + hL[1] + hR[1]) / 4];
  const neck = [(sL[0] + sR[0]) / 2, (sL[1] + sR[1]) / 2];
  const [hx, hy] = p.head;
  g.fillStyle = 'rgba(0,0,0,.32)'; g.beginPath(); g.ellipse((p.an[0][0] + p.an[1][0]) / 2, Math.max(p.an[0][1], p.an[1][1]) + 8, 78, 9, 0, 0, Math.PI * 2); g.fill();
  limb(hL, p.kn[0], p.an[0], 17); limb(hR, p.kn[1], p.an[1], 17);
  g.fillStyle = SHOE;
  g.beginPath(); g.ellipse(p.an[0][0] - 6, p.an[0][1] + 5, 12, 6.5, 0, 0, Math.PI * 2); g.fill();
  g.beginPath(); g.ellipse(p.an[1][0] + 6, p.an[1][1] + 5, 12, 6.5, 0, 0, Math.PI * 2); g.fill();
  poly([[hL[0] - 7, hL[1] - 4], [hR[0] + 7, hR[1] - 4], [tR[0]! + 9, tR[1]!], crotch, [tL[0]! - 9, tL[1]!]], SHORTS, 6);
  g.strokeStyle = LIMB; g.lineWidth = 12; g.lineCap = 'round'; g.beginPath(); g.moveTo(neck[0]!, neck[1]!); g.lineTo(hx, hy + 14); g.stroke();
  poly([[sL[0] - 3, sL[1]], [sR[0] + 3, sR[1]], [hR[0] + 4, hR[1] - 2], [hL[0] - 4, hL[1] - 2]], JERSEY, 16);
  g.strokeStyle = TRIM; g.lineWidth = 3; g.beginPath(); g.moveTo(sL[0] + 8, sL[1] - 6); g.quadraticCurveTo(neck[0]!, neck[1]! + 12, sR[0] - 8, sR[1] - 6); g.stroke();
  g.save(); g.globalAlpha = 0.92; text(g, num, mid[0]!, mid[1]! + 11, `400 30px ${DISPLAY}`, '#FFFFFF', 'center'); g.restore();
  limb(sL, p.el[0], p.ha[0], 13); limb(sR, p.el[1], p.ha[1], 13);
  g.fillStyle = LIMB;
  g.beginPath(); g.arc(p.ha[0][0], p.ha[0][1], 9, 0, Math.PI * 2); g.fill();
  g.beginPath(); g.arc(p.ha[1][0], p.ha[1][1], 9, 0, Math.PI * 2); g.fill();
  g.beginPath(); g.arc(hx, hy, 19.5, 0, Math.PI * 2); g.fill();
  g.strokeStyle = TRIM; g.lineWidth = 6; g.beginPath(); g.moveTo(hx - 18, hy - 6); g.lineTo(hx + 18, hy - 6); g.stroke();
}
/** L'image dessinée du mode d'essai : la pièce, puis la silhouette ; au niveau 4, le ballon qui rebondit. */
export function drawSimCamera(g: G, pose: Pose, ball?: Pt | null): void {
  g.drawImage(roomCanvas(), 0, 0, CAM_W, CAM_H);
  drawBody(g, pose);
  if (ball) {
    g.save();
    g.fillStyle = '#C2410C'; g.beginPath(); g.arc(ball.x, ball.y, 16, 0, Math.PI * 2); g.fill();
    g.strokeStyle = '#17191C'; g.lineWidth = 1.5; g.beginPath();
    g.moveTo(ball.x - 16, ball.y); g.lineTo(ball.x + 16, ball.y); g.moveTo(ball.x, ball.y - 16); g.lineTo(ball.x, ball.y + 16); g.stroke();
    g.restore();
  }
}
